import { useEffect, useRef } from 'react'
import { Delete } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * On-screen PIN pad for touch terminals.
 *
 * Replaces the four-box split input the PIN modals used to draw. That pattern
 * hopped focus in JS after every digit (`inputRefs[i+1].focus()`), and on a
 * Windows touchscreen the keyboard shows on a TOUCH-initiated focus and hides
 * on blur — a programmatic focus is deliberately not a show trigger. So the
 * keyboard closed after every keystroke and the manager had to re-summon it
 * once per digit, mid-service, to void a line.
 *
 * The fix is not a better focus dance: it is not needing the OS keyboard at
 * all. This renders NO text input, so nothing can summon TabTip. Digits come
 * from real buttons, and a physical keyboard still works because the container
 * itself takes focus and handles keydown — the desk terminals that have a
 * keyboard lose nothing.
 */

/** Keys the pad understands. Digits are '0'–'9'. */
export type PinPadKey = string

/**
 * Apply one key press to the current value.
 *
 * Pure so the rules are testable without rendering: a digit appends until the
 * PIN is full (further digits are ignored rather than silently shifting the
 * value), backspace drops the last, clear empties it.
 */
export function applyPinKey(current: string, key: PinPadKey, length: number): string {
  if (key === 'backspace') return current.slice(0, -1)
  if (key === 'clear') return ''
  if (/^\d$/.test(key)) return current.length >= length ? current : current + key
  return current
}

interface PinPadProps {
  value: string
  onChange: (next: string) => void
  /** Fired when the PIN reaches full length via Enter, not on every digit. */
  onSubmit?: () => void
  length?: number
  disabled?: boolean
  /** Shake the dots to signal a rejected PIN. */
  invalid?: boolean
  className?: string
}

const KEYS: Array<{ key: PinPadKey; label: string }> = [
  { key: '1', label: '1' }, { key: '2', label: '2' }, { key: '3', label: '3' },
  { key: '4', label: '4' }, { key: '5', label: '5' }, { key: '6', label: '6' },
  { key: '7', label: '7' }, { key: '8', label: '8' }, { key: '9', label: '9' },
]

export function PinPad({
  value,
  onChange,
  onSubmit,
  length = 4,
  disabled = false,
  invalid = false,
  className,
}: PinPadProps) {
  const hostRef = useRef<HTMLDivElement>(null)

  // Take focus on mount so a physical keyboard works immediately. This is a
  // div, not an input, so focusing it cannot raise the on-screen keyboard.
  useEffect(() => {
    hostRef.current?.focus()
  }, [])

  const press = (key: PinPadKey) => {
    if (disabled) return
    onChange(applyPinKey(value, key, length))
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    if (e.key === 'Enter') {
      e.preventDefault()
      onSubmit?.()
      return
    }
    if (e.key === 'Backspace') {
      e.preventDefault()
      press('backspace')
      return
    }
    if (/^\d$/.test(e.key)) {
      e.preventDefault()
      press(e.key)
    }
  }

  return (
    <div
      ref={hostRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      role="group"
      aria-label={`Enter ${length}-digit PIN`}
      className={cn('outline-none', className)}
    >
      {/* Filled-dot readout. Deliberately not an input: an input is what the
          OS keyboard attaches to, and this pad exists to avoid that. */}
      <div
        className={cn('flex justify-center gap-3', invalid && 'animate-shake')}
        aria-hidden
      >
        {Array.from({ length }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-xl border-2 text-2xl font-bold transition-colors',
              i < value.length
                ? 'border-ring bg-accent text-foreground'
                : 'border-input bg-background text-muted-foreground'
            )}
          >
            {i < value.length ? '•' : ''}
          </div>
        ))}
      </div>
      <p className="sr-only" aria-live="polite">
        {value.length} of {length} digits entered
      </p>

      <div className="mx-auto mt-5 grid max-w-[15rem] grid-cols-3 gap-2.5">
        {KEYS.map((k) => (
          <PadButton key={k.key} onPress={() => press(k.key)} disabled={disabled} label={k.label}>
            {k.label}
          </PadButton>
        ))}
        <PadButton onPress={() => press('clear')} disabled={disabled} label="Clear PIN" muted>
          <span className="text-sm font-semibold">Clear</span>
        </PadButton>
        <PadButton onPress={() => press('0')} disabled={disabled} label="0">
          0
        </PadButton>
        <PadButton onPress={() => press('backspace')} disabled={disabled} label="Delete last digit" muted>
          <Delete className="h-5 w-5" />
        </PadButton>
      </div>
    </div>
  )
}

function PadButton({
  onPress,
  disabled,
  label,
  muted,
  children,
}: {
  onPress: () => void
  disabled: boolean
  label: string
  muted?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      // The pad owns keyboard handling at the container; taking focus per key
      // would move it off the host and break physical typing after one tap.
      tabIndex={-1}
      onClick={onPress}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'flex h-14 items-center justify-center rounded-xl border text-2xl font-semibold',
        'transition-colors active:scale-[0.97] disabled:opacity-40',
        muted
          ? 'border-input bg-muted text-muted-foreground hover:bg-muted/80'
          : 'border-input bg-background text-foreground hover:bg-accent'
      )}
    >
      {children}
    </button>
  )
}
